import { AxiosError } from 'axios';
import React from 'react';
import styled from 'styled-components';

import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import RadioButton from '@amzn/meridian/radio-button';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';

import { UamUpdateRequestOperationEnum, UamUpdateResponse } from '../../../trustAdmin-api/generated-src';
import TrustAdminApiFactory from '../../../trustAdmin-api/TrustAdminApiFactory';
import { formatTimestamp } from '../../../utils/formatTimestamp';

export interface UamDetails {
    enabled: boolean;
    adminAlias: string;
    note: string;
    updatedAt: string;
}

const SECTION_HEADER = 'Unusual Activity Message';
const NOTE_MAX_LENGTH = 500;
const NOTE_LABEL = 'Note (required)';
const NOTE_PLACEHOLDER = `Enter a note (max ${NOTE_MAX_LENGTH} characters)`;

const MSG_RRC_CONFLICT = 'UAM cannot be enabled while RRC is active for this title';
const MSG_ALREADY_ENABLED = 'already active';
const MSG_ALREADY_DISABLED = 'not active';

interface UamDetailsSectionState {
    enabled: boolean;
    lastUpdatedAt: string | undefined;
    lastUpdatedBy: string | undefined;
    modalOpen: boolean;
    note: string;
    loading: boolean;
    alertMessage: string | undefined;
    alertType: 'error' | 'warning' | 'success';
}

export interface UamDetailsSectionProps {
    tconst: string;
    titleName?: string;
    uamDetails?: UamDetails | null;
}

const buildInitialState = (uamDetails?: UamDetails | null): UamDetailsSectionState => ({
    enabled: uamDetails?.enabled ?? false,
    lastUpdatedAt: uamDetails?.updatedAt,
    lastUpdatedBy: uamDetails?.adminAlias,
    modalOpen: false,
    note: '',
    loading: false,
    alertMessage: undefined,
    alertType: 'error'
});

export const UamDetailsSection: React.FC<UamDetailsSectionProps> = ({ tconst, titleName, uamDetails }) => {
    const [state, setState] = React.useState<UamDetailsSectionState>(() => buildInitialState(uamDetails));

    React.useEffect(() => {
        setState((prev) => ({
            ...prev,
            enabled: uamDetails?.enabled ?? false,
            lastUpdatedAt: uamDetails?.updatedAt,
            lastUpdatedBy: uamDetails?.adminAlias
        }));
    }, [uamDetails]);

    const { enabled, lastUpdatedAt, lastUpdatedBy, modalOpen, note, loading, alertMessage, alertType } = state;

    const pendingOperation = enabled ? UamUpdateRequestOperationEnum.Remove : UamUpdateRequestOperationEnum.Add;

    const modalHeader = pendingOperation === UamUpdateRequestOperationEnum.Add ? 'Enable UAM' : 'Disable UAM';

    const modalDescription =
        pendingOperation === UamUpdateRequestOperationEnum.Add
            ? `You are about to enable UAM for ${tconst}.`
            : `You are about to disable UAM for ${tconst}.`;

    const handleToggleClick = () => {
        setState((prev) => ({ ...prev, alertMessage: undefined, note: '', modalOpen: true }));
    };

    const handleCancel = () => {
        setState((prev) => ({ ...prev, modalOpen: false, note: '', alertMessage: undefined }));
    };

    const handleConfirm = async () => {
        setState((prev) => ({ ...prev, loading: true, alertMessage: undefined }));

        try {
            const response = await TrustAdminApiFactory().unusualActivity.updateUnusualActivity({
                tconst,
                titleName,
                operation: pendingOperation,
                note
            });

            const data: UamUpdateResponse = response.data;
            setState((prev) => ({
                ...prev,
                enabled: data.enabled,
                modalOpen: false,
                note: '',
                lastUpdatedAt: new Date().toISOString(),
                lastUpdatedBy: undefined,
                loading: false
            }));
        } catch (e) {
            const error = e as AxiosError;
            if (error.response?.status === 409) {
                const message =
                    typeof error.response.data === 'string'
                        ? error.response.data
                        : 'A conflict occurred. Please try again.';

                if (message.toLowerCase().includes('rrc')) {
                    setState((prev) => ({
                        ...prev,
                        alertType: 'warning',
                        alertMessage: MSG_RRC_CONFLICT,
                        loading: false
                    }));
                } else if (
                    message.toLowerCase().includes(MSG_ALREADY_ENABLED) ||
                    message.toLowerCase().includes(MSG_ALREADY_DISABLED)
                ) {
                    setState((prev) => ({ ...prev, alertType: 'warning', alertMessage: message, loading: false }));
                } else {
                    setState((prev) => ({ ...prev, alertType: 'error', alertMessage: message, loading: false }));
                }
            } else {
                setState((prev) => ({
                    ...prev,
                    alertType: 'error',
                    alertMessage: 'An unexpected error occurred. Please try again.',
                    loading: false
                }));
            }
        }
    };

    const handleNoteChange = (value: string) => {
        if (value.length <= NOTE_MAX_LENGTH) {
            setState((prev) => ({ ...prev, note: value }));
        }
    };

    const isConfirmDisabled = loading || note.trim().length === 0;

    const formattedTimestamp = lastUpdatedAt ? formatTimestamp(lastUpdatedAt) : 'N/A';

    return (
        <SectionContainer data-testid='uam-details-section'>
            <Text type='h100'>{SECTION_HEADER}</Text>
            <Column spacing='200'>
                <Text type='b200'>Status:</Text>
                <Column spacing='100'>
                    <RadioButton checked={enabled} onChange={handleToggleClick} name='uam-status' value='enabled'>
                        Enabled
                    </RadioButton>
                </Column>
                <Column spacing='100'>
                    <RadioButton checked={!enabled} onChange={handleToggleClick} name='uam-status' value='disabled'>
                        Disabled
                    </RadioButton>
                </Column>
            </Column>
            <Text type='b200' color='secondary'>
                {`Last updated: ${formattedTimestamp}${lastUpdatedBy ? ` by ${lastUpdatedBy}` : ''}`}
            </Text>

            {alertMessage && !modalOpen && (
                <Alert type={alertType} data-testid='uam-alert'>
                    {alertMessage}
                </Alert>
            )}

            <Modal
                title={modalHeader}
                open={modalOpen}
                onClose={handleCancel}
                scrollContainer='modal'
                closeLabel='Close'
                width={480}
                data-testid='uam-modal'
            >
                <Column spacing='300'>
                    <Text type='b200'>{modalDescription}</Text>
                    <Column spacing='100'>
                        <Text type='b200' tag='label'>
                            {NOTE_LABEL}
                        </Text>
                        <Textarea
                            value={note}
                            onChange={handleNoteChange}
                            placeholder={NOTE_PLACEHOLDER}
                            rows={4}
                            data-testid='uam-note-input'
                        />
                        <Text type='b100' color='secondary'>
                            {`${note.length}/${NOTE_MAX_LENGTH} characters`}
                        </Text>
                    </Column>
                    {alertMessage && modalOpen && (
                        <Alert type={alertType} data-testid='uam-modal-alert'>
                            {alertMessage}
                        </Alert>
                    )}
                </Column>
                <ModalFooter>
                    <Row alignmentHorizontal='end' widths='fit'>
                        <Button type='secondary' onClick={handleCancel} data-testid='uam-cancel-button'>
                            Cancel
                        </Button>
                        <Button
                            type='primary'
                            onClick={handleConfirm}
                            disabled={isConfirmDisabled}
                            data-testid='uam-confirm-button'
                        >
                            {loading ? <Loader type='linear' size='small' /> : 'Confirm'}
                        </Button>
                    </Row>
                </ModalFooter>
            </Modal>
        </SectionContainer>
    );
};

const SectionContainer = styled.div`
    margin-top: 16px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    background-color: #f7f9fc;
    border: 1px solid #e8ecf0;
    border-radius: 8px;
`;
